import * as Yup from 'yup';
import { TFunction } from 'i18next';
import _get from 'lodash/get';
import dayjs from 'dayjs';
import {
  TaskTimelineCompletionGroupByEnum,
  TaskTimelineFrameEnum,
} from 'graphql-common';
import Input from '@lib/components/Input/Input';
import { InputTypes } from '@lib/components/Input/enums';
import RadioButtonGroup, {
  RadioButtonWrapViews,
} from '@lib/components/RadioButtonGroup/RadioButtonGroup';
import Select, { SelectComponents } from '@lib/components/Select/Select';
import Trans from '@lib/components/Trans/Trans';
import {
  getDateRangeValidation,
  getFieldValidation,
  getOptionsArrayValidation,
  getOptionValidation,
} from '@lib/utils/validationUtils';
import { GetLoadOptions, SelectOption } from '@lib/components/Select/types';
import { FieldItem, Option, Values } from '@lib/interfaces/form';
import Typography from '@lib/components/Typography/Typography';
import React from 'react';
import RadioButtonAsButton from '@lib/components/RadioButtonAsButton/RadioButtonAsButton';
import { ButtonSizes } from '@lib/components/Button/Button';
import SupportiveMessageBox from '@lib/components/SupportiveMessage/SupportiveMessageBox';
import DateRangePicker from '@lib/components/DateTimePicker/DateRangePicker';
import TasksFilterFieldNames from '@lib/enums/fieldNames/tasksFilterFieldNames';
import { getTaskTimelineViewOption } from 'utils/getTaskTimelineViewOptions';
import formStyles from '@lib/assets/styles/Form.module.scss';
import Tooltip from '@lib/components/Tooltip/Tooltip';
import MaterialIcon from '@lib/components/MaterialIcon/MaterialIcon';
import classNames from 'classnames';
import UserOption from '@lib/components/Select/components/UserOption';
import UserMultiValueContainer from '@lib/components/Select/components/UserMultiValueContainer';
import { MAX_DELIVERY_RECIPIENTS } from '@lib/constants/delivery';
import styles from '../GenerateReportForm.module.scss';
import { FieldNames, FormTypeValue } from '../types';

export function getFormTypeOptions(): Option[] {
  return [
    { value: FormTypeValue.Download, labelKey: 'download' },
    { value: FormTypeValue.Email, labelKey: 'send-via-email' },
  ];
}

export function getCompletionGroupByOptions(): Option[] {
  return [
    {
      value: TaskTimelineCompletionGroupByEnum.SiteArea,
      labelKey: 'area',
    },
    {
      value: TaskTimelineCompletionGroupByEnum.FormCategory,
      labelKey: 'form-category',
    },
    {
      value: TaskTimelineCompletionGroupByEnum.FormTemplate,
      labelKey: 'form-name',
    },
  ];
}

export default function getFormFields(args: {
  getUsersLoadOptions: GetLoadOptions;
  t: TFunction<'translation', undefined>;
  filterParams: Values;
  basePeriodStartParam: string;
  basePeriodEndParam: string;
}): FieldItem[] {
  const {
    getUsersLoadOptions,
    t,
    filterParams,
    basePeriodStartParam,
    basePeriodEndParam,
  } = args;
  return [
    {
      name: FieldNames.FormType,
      label: '',
      element: RadioButtonGroup,
      validation: getOptionValidation({ required: true, t }),
      componentProps: {
        options: getFormTypeOptions(),
        RadioButtonComponent: RadioButtonAsButton,
        wrapView: RadioButtonWrapViews.row,
        wrapClass: styles.formTypeSwitcher,
      },
    },
    {
      name: FieldNames.Period,
      label: t('period'),
      element: DateRangePicker,
      validation: getDateRangeValidation(true, t),
      componentProps: {
        type: InputTypes.dateRange,
        minDate: dayjs(basePeriodStartParam).startOf('day'),
        maxDate: dayjs(basePeriodEndParam).endOf('day'),
      },
      isOptionalFn: () => false,
      formItemClassName: formStyles.formColumn,
    },
    {
      name: FieldNames.Title1,
      customRender: () => (
        <Typography variant="h3" key="title-1">
          {t('report-based-on-applied-filters')}:
        </Typography>
      ),
    },
    {
      name: FieldNames.ReportDetails,
      customRender: () => {
        const timelineView = getTaskTimelineViewOption(
          _get(
            filterParams,
            TasksFilterFieldNames.TimelineView,
          ) as TaskTimelineFrameEnum,
        )?.labelKey;
        const sites = _get(
          filterParams,
          TasksFilterFieldNames.Site,
        ) as SelectOption[];
        const formCategories = _get(
          filterParams,
          TasksFilterFieldNames.FormCategory,
        ) as SelectOption[];
        const validityHints = _get(
          filterParams,
          TasksFilterFieldNames.ValidityHints,
        ) as SelectOption[];
        return (
          <SupportiveMessageBox>
            <div>
              <Typography variant="caption">
                {t('timeframe')}:{' '}
                <strong>{timelineView ? t(timelineView) : '-'}</strong>
                <br />
                {t('sites-label')}: <strong>{sites?.length || t('all')}</strong>
                <br />
                {t('form-categories-label')}:{' '}
                <strong>{formCategories?.length || t('all')}</strong>
                <br />
                {t('completion-statuses-label')}:{' '}
                <strong>
                  {validityHints
                    ? validityHints
                        .map(({ labelKey }) => (labelKey ? t(labelKey) : '-'))
                        .join(', ')
                    : t('all')}
                </strong>
                <br />
              </Typography>
            </div>
          </SupportiveMessageBox>
        );
      },
    },
    {
      name: FieldNames.Title2,
      customRender: () => (
        <div className={classNames(formStyles.fieldTitle, styles.fieldTitle)}>
          <Typography variant="h3" key="title-2">
            {t('group-data-by')}
          </Typography>
          <div className={formStyles.fieldTitleIcon}>
            <Tooltip body={<Trans i18nKey="group-data-by-desc" />}>
              <MaterialIcon icon="info" symbolsOutlined size="md-16" />
            </Tooltip>
          </div>
        </div>
      ),
    },
    {
      name: FieldNames.CompletionGroupBy,
      element: RadioButtonGroup,
      validation: getOptionValidation({ required: true, t }),
      componentProps: {
        options: getCompletionGroupByOptions(),
        RadioButtonComponent: RadioButtonAsButton,
        radioButtonComponentProps: {
          fullWidth: true,
          buttonSize: ButtonSizes.small,
        },
        wrapView: RadioButtonWrapViews.row,
      },
    },
    {
      name: FieldNames.Title3,
      customRender: () => (
        <Typography variant="h3" key="title-2">
          {t('recipient-data')}
        </Typography>
      ),
      isVisibleFn: (values: Values, name?: string) => {
        if (name) {
          return (
            _get(values, [FieldNames.FormType, 'value']) === FormTypeValue.Email
          );
        }
        return true;
      },
    },
    {
      name: FieldNames.RecipientIds,
      label: t('recipient-emails'),
      element: Select,
      componentProps: {
        isMulti: true,
        isAsync: true,
        defaultOptions: false,
        usePortal: true,
        getLoadOptions: getUsersLoadOptions,
        components: {
          Option: UserOption,
          MultiValueContainer: UserMultiValueContainer,
        } as unknown as SelectComponents,
      },
      validation: Yup.array().when(FieldNames.FormType, (deps, schema) =>
        _get(deps, [0, 'value']) === FormTypeValue.Email
          ? getOptionsArrayValidation({ t, required: true }).max(
              MAX_DELIVERY_RECIPIENTS,
              t('max-recipients-field-error'),
            )
          : schema.nullable(),
      ),
      isOptionalFn: (values: Values) =>
        _get(values, [FieldNames.FormType, 'value']) !== FormTypeValue.Email,
      isVisibleFn: (values: Values, name?: string) => {
        if (name) {
          return (
            _get(values, [FieldNames.FormType, 'value']) === FormTypeValue.Email
          );
        }
        return true;
      },
    },
    {
      name: FieldNames.Message,
      label: t('message'),
      element: Input,
      componentProps: {
        type: InputTypes.textarea,
      },
      validation: getFieldValidation({ t }),
      isVisibleFn: (values: Values, name?: string) => {
        if (name) {
          return (
            _get(values, [FieldNames.FormType, 'value']) === FormTypeValue.Email
          );
        }
        return true;
      },
    },
  ];
}
